import React, { useEffect, useMemo, useState } from "react";
import "./index.less";
import { ChatLayoutContainer } from "../ChatLayoutContainer";
import { ContactList, ContentTabsTop, ListEmpty, withMessage } from "remo-components";
import { useTranslate } from "@obsbot/remo-i18n";
import { Badge, Button, Input, Space } from "antd";
import { useFriendStore, useGlobalStore } from "@obsbot/remo-stores";
import { AddFriend, getApplicationList, readApplicationMsg } from "@obsbot/remo-server-api";
import { ApplicationProps } from "@obsbot/remo-services";
import { CloseOutlined } from "@ant-design/icons";
enum SendStatus {
    UnSend = "unsend",
    Sending = "sending",
    SendSuccess = "success",
    SendFail = "error",
    RTM = "rtm", // rtm 获取失败
}

const AddFriendsPage: React.FC = (): React.ReactElement => {
    const t = useTranslate();
    const [currentTab, setCurrentTab] = useState<string>("addFriends");
    const [currentType, setCurrentType] = useState<"apply-in" | "apply-out">("apply-in");
    const [sendStatus, setSendStatus] = useState<SendStatus>(SendStatus.UnSend);
    const [searchValue, setSearchValue] = useState<string | undefined>(); // 搜索输入框的值
    const [sentValue, setSentValue] = useState<string | undefined>(); // 保存发送时的值
    const { application, updateApplicationList, friendList } = useFriendStore();
    const { userInfo } = useGlobalStore();
    const [tip, setTip] = useState<string>("");

    const tabItems = useMemo(() => {
        return [
            {
                key: "addFriends",
                label: t("chat.add-friend"),
            },
            {
                key: "friendsApply",
                label: (
                    <Badge count={application.unreadCount} dot={true} offset={[1, -5]} size="small">
                        {t("chat.friend-apply")}
                    </Badge>
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [application.unreadCount]);

    const applicationItems = useMemo(() => {
        const items = [
            {
                key: "apply-in",
                label: t("chat.apply-friend-receive", {
                    count: application.receivedRequests.length,
                }),
                hasData: application.receivedRequests.length > 0,
            },
            {
                key: "apply-out",
                label: t("chat.apply-friend-sent", {
                    count: application.sentRequests.length,
                }),
                hasData: application.sentRequests.length > 0,
            },
        ];
        // 优先显示有数据的选项
        return items.sort((a, b) => {
            if (a.hasData && !b.hasData) {
                return -1;
            }
            if (!a.hasData && b.hasData) {
                return 1;
            }
            return 0; // 都有数据或都没数据时保持原顺序
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        application.receivedRequests.length,
        application.sentRequests.length,
        currentType,
        currentTab,
    ]);

    const handleChangeTab = async (tabKey: string): Promise<void> => {
        setCurrentTab(tabKey);
        if (tabKey === "friendsApply") {
            // 根据数据情况选择默认子标签，保持与 ContentTabsTop 初始选中一致
            const nextDefaultType =
                application.receivedRequests.length > 0
                    ? "apply-in"
                    : application.sentRequests.length > 0
                      ? "apply-out"
                      : "apply-in";
            setCurrentType(nextDefaultType);
            if (application.unreadCount > 0) {
                const res = await readApplicationMsg();
                if (res.code === "200") {
                    updateApplicationList(res.data as ApplicationProps);
                }
            }
        }
    };

    const handleApplication = async (): Promise<void> => {
        if (!searchValue) {
            setSendStatus(SendStatus.SendFail);
            setTip(t("chat.add-friend-tips-empty"));
            return;
        }
        if (!userInfo) {
            setSendStatus(SendStatus.SendFail);
            setTip(t("chat.add-friend-tips-no-user"));
            return;
        }
        const trimmedValue = searchValue.trim();
        try {
            const isSuccess = await AddFriend({
                email: trimmedValue,
                senderNickname: userInfo.nickname,
                senderLogo: userInfo.logo,
                relationType: 0, // 添加普通好友 默认传值为0
                blockMsgBox: true,
            });
            if (isSuccess) {
                setSentValue(trimmedValue); // 保存发送时的值
                setSearchValue(""); // 清空输入框
                setSendStatus(SendStatus.SendSuccess);
                getApplications();
                setTip(t("chat.add-friend-tips-" + SendStatus.SendSuccess, { user: trimmedValue }));
            } else {
                setSentValue(trimmedValue); // 失败时也保存发送时的值
                setSendStatus(SendStatus.SendFail);
                setTip(t("chat.add-friend-tips-" + SendStatus.SendFail, { user: sentValue }));
            }
        } catch (error) {
            setSentValue(trimmedValue); // 失败时也保存发送时的值
            setSendStatus(SendStatus.SendFail);
            setTip(t("error_code." + error.code));
        }
    };

    const getApplications = async (): Promise<void> => {
        const res = await getApplicationList();
        if (res?.code === "200") {
            updateApplicationList(res.data as ApplicationProps);
        } else {
            console.log("[ERROR]: get friend application failed");
        }
    };

    const changeSearchValue = (e: any): void => {
        setSearchValue(e.currentTarget.value);
    };

    const changeApply = (key: string): void => {
        setCurrentType(key as "apply-in" | "apply-out");
    };

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (sendStatus === SendStatus.SendSuccess || sendStatus === SendStatus.SendFail) {
            timer = setTimeout(() => {
                setSendStatus(SendStatus.UnSend);
                setSentValue(undefined); // 清除保存的发送值
            }, 10000);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [sendStatus]);

    return (
        <ChatLayoutContainer>
            <div className="add-friends-page">
                <ContentTabsTop handleChangeTab={handleChangeTab} tabItems={tabItems} />
                {currentTab === "addFriends" && (
                    <div className="add-friends-content">
                        <div className="add-friend-desc">{t("chat.add-friend-desc")}</div>
                        <div className="add-friend-search-group">
                            <Space style={{ width: "100%" }}>
                                <Space.Compact
                                    className={"search-input-compact status-" + sendStatus}
                                    tabIndex={0}
                                >
                                    <Input
                                        allowClear={{
                                            clearIcon: (
                                                <CloseOutlined
                                                    className="closable-icon"
                                                    style={{
                                                        color: "var(--normal-white)",
                                                        opacity: 0.6,
                                                    }}
                                                />
                                            ),
                                        }}
                                        className="search-input"
                                        placeholder={t("chat.add-friend-desc")}
                                        value={searchValue}
                                        onChange={changeSearchValue}
                                    />
                                    <Button
                                        className="search-btn"
                                        type="primary"
                                        onClick={handleApplication}
                                    >
                                        {t("chat.add-friend-request")}
                                    </Button>
                                </Space.Compact>
                            </Space>
                        </div>
                        <div className={`add-friend-tips tips-${sendStatus}`}>
                            {(sendStatus === SendStatus.SendFail ||
                                sendStatus === SendStatus.SendSuccess) &&
                                tip}
                        </div>
                        {friendList.length === 0 && <ListEmpty modeType="list-all" />}
                    </div>
                )}
                {currentTab === "friendsApply" &&
                (application.sentRequests.length > 0 || application.receivedRequests.length > 0) ? (
                    <div className="apply-content">
                        <ContentTabsTop handleChangeTab={changeApply} tabItems={applicationItems} />
                        <ContactList modeType={currentType} />
                    </div>
                ) : (
                    currentTab === "friendsApply" && (
                        <div className="chat-empty-apply">
                            <ListEmpty modeType="apply-in" />
                            {/* {t("chat.empty-apply")} */}
                        </div>
                    )
                )}
            </div>
        </ChatLayoutContainer>
    );
};

export default withMessage(AddFriendsPage);
